import {
  hasModulePermission,
  isSaasAdmin,
  type PermissionLevelValue,
  type PermissionModule,
} from '@crm/shared';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  BadgeCheck,
  BadgePercent,
  Bot,
  Building2,
  CalendarDays,
  ChartNoAxesColumn,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Contact,
  CreditCard,
  FileSpreadsheet,
  FolderClosed,
  Globe,
  HandCoins,
  Headset,
  KanbanSquare,
  Layers,
  LayoutDashboard,
  MapPin,
  Megaphone,
  MessageSquare,
  Palette,
  Receipt,
  ScanSearch,
  Scissors,
  ScrollText,
  Settings,
  ShieldCheck,
  Tags,
  Target,
  Ticket,
  User,
  UserRound,
  Users,
  UsersRound,
  Wallet,
  Wand2,
  Zap,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { AUTH_ROUTES } from '@/modules/auth/constants';
import { useSession } from '@/modules/auth/hooks';
import { AUTOMATIONS_ROUTES } from '@/modules/automations';
import { BILLING_ROUTES } from '@/modules/billing';
import { BOARD_ROUTES } from '@/modules/board';
import { BOT_ROUTES } from '@/modules/bot/api';
import { CAMPAIGNS_ROUTES } from '@/modules/campaigns/api';
import { CASHBACK_ROUTES } from '@/modules/cashback/api';
import { CATALOG_ROUTES } from '@/modules/catalog/api';
import { COMMISSIONS_ROUTES } from '@/modules/commissions/api';
import { COMPANY_ROUTES } from '@/modules/company/constants';
import { CONTACT_LISTS_ROUTES } from '@/modules/contact-lists/api';
import { CONVERSATIONS_ROUTES } from '@/modules/conversations/api';
import { COUPONS_ROUTES } from '@/modules/coupons/api';
import { CUSTOMERS_ROUTES } from '@/modules/customers/api';
import { FINANCE_ROUTES } from '@/modules/finance/api';
import { LANDING_ROUTES } from '@/modules/landing/api';
import { LEADS_ROUTES } from '@/modules/leads/api';
import { MANAGEMENT_ROUTES } from '@/modules/management/api';
import { MARKETING_ROUTES } from '@/modules/marketing';
import { PROFILES_ROUTES } from '@/modules/profiles/constants';
import { RECEIVABLES_ROUTES } from '@/modules/receivables/api';
import { SAAS_ROUTES } from '@/modules/saas/api';
import { SCHEDULE_ROUTES } from '@/modules/schedule/api';
import { SETTINGS_ROUTES } from '@/modules/settings/constants';
import { SUPPORT_ROUTES } from '@/modules/support';
import { USERS_ROUTES } from '@/modules/users/constants';
import { useLayoutStore } from './use-layout-store';

export interface NavItem {
  to: string;
  /** Diz a tela inteira: "Contas a pagar", nao "A pagar". */
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Quando presente, o item so aparece se o usuario tiver o nivel exigido. */
  requires?: { module: PermissionModule; level: PermissionLevelValue };
  end?: boolean;
  /** Titulo do bloco dentro do submenu - itens seguidos com o mesmo valor. */
  group?: string;
  /** Abre em outra aba: a tela nao faz parte do sistema, e olhar nao e sair. */
  external?: boolean;
}

export interface NavSection {
  /** Mesma regra dos itens: sem o nivel exigido, a secao nao aparece. */
  requires?: { module: PermissionModule; level: PermissionLevelValue };
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  /**
   * Secao que e um destino, nao um grupo: o bloco navega direto e nao abre
   * submenu. E o caso do painel e das telas de quem administra o SaaS.
   */
  to?: string;
  /** Rota exata. So o painel precisa: sem isso, "/" marcaria toda tela. */
  end?: boolean;
  items: NavItem[];
}

/**
 * As telas do sistema, na ordem em que aparecem no menu. Fonte unica: a
 * navegacao lateral e a busca da barra do topo leem daqui.
 */
export const SECTIONS: NavSection[] = [
  {
    title: 'Painel',
    icon: LayoutDashboard,
    to: AUTH_ROUTES.DASHBOARD,
    end: true,
    // Quem nao ve relatorio nao ve o painel: ele e todo feito deles.
    requires: { module: 'reports', level: 'READ' },
    items: [],
  },
  {
    title: 'Operacao',
    icon: Zap,
    items: [
      {
        to: SCHEDULE_ROUTES.AGENDA,
        label: 'Agenda',
        icon: CalendarDays,
        requires: { module: 'schedule', level: 'READ' },
      },
      {
        // "Checkin" e o que se faz ali: receber quem chegou. Tambem libera
        // "Atendimentos" para o relatorio, sem dois itens de mesmo nome.
        to: SCHEDULE_ROUTES.SERVICE_DESK,
        label: 'Checkin',
        icon: ClipboardCheck,
        requires: { module: 'appointments', level: 'READ' },
      },
      {
        to: CONVERSATIONS_ROUTES.LIST,
        label: 'WhatsApp',
        icon: MessageSquare,
        requires: { module: 'appointments', level: 'READ' },
      },
      {
        to: LEADS_ROUTES.BOARD,
        label: 'Funil de vendas',
        icon: Target,
        requires: { module: 'appointments', level: 'READ' },
      },
    ],
  },
  {
    title: 'Cadastros',
    icon: FolderClosed,
    items: [
      {
        to: CUSTOMERS_ROUTES.LIST,
        label: 'Clientes',
        icon: UserRound,
        requires: { module: 'customers', level: 'READ' },
      },
      {
        to: CATALOG_ROUTES.SERVICES,
        label: 'Servicos',
        icon: Scissors,
        requires: { module: 'services', level: 'READ' },
      },
      {
        to: CATALOG_ROUTES.CATEGORIES,
        label: 'Categorias de servico',
        icon: Tags,
        requires: { module: 'services', level: 'READ' },
      },
    ],
  },
  {
    // O que traz cliente novo e o que traz cliente de volta, do que responde
    // sozinho ate a base que as campanhas usam.
    title: 'Marketing',
    icon: Megaphone,
    items: [
      {
        to: BOT_ROUTES.CONFIG,
        label: 'Assistente do WhatsApp',
        icon: Bot,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        to: CAMPAIGNS_ROUTES.LIST,
        label: 'Campanhas',
        icon: Megaphone,
        requires: { module: 'customers', level: 'READ' },
      },
      {
        to: AUTOMATIONS_ROUTES.LIST,
        label: 'Mensagens automaticas',
        icon: MessageSquare,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        to: LANDING_ROUTES.CONFIG,
        label: 'LandingPage do Salao',
        icon: Globe,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        to: CONTACT_LISTS_ROUTES.LIST,
        label: 'Listas de contatos',
        icon: Contact,
        requires: { module: 'customers', level: 'WRITE' },
      },
    ],
  },
  {
    title: 'Fidelidade',
    icon: BadgePercent,
    items: [
      {
        to: CASHBACK_ROUTES.RULES,
        label: 'Regras de cashback',
        icon: BadgePercent,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: CASHBACK_ROUTES.ENTRIES,
        label: 'Extrato de cashback',
        icon: Coins,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: COUPONS_ROUTES.LIST,
        label: 'Cupons',
        icon: Ticket,
        requires: { module: 'finance', level: 'READ' },
      },
    ],
  },
  {
    title: 'Financeiro',
    icon: CircleDollarSign,
    // A ordem segue o caminho de quem usa: classifica, cadastra a conta e so
    // depois paga e recebe.
    items: [
      {
        to: FINANCE_ROUTES.CATEGORIES,
        label: 'Categorias financeiras',
        icon: Layers,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: FINANCE_ROUTES.BILLS,
        label: 'Cadastro de contas',
        icon: Receipt,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: FINANCE_ROUTES.PAYABLES,
        label: 'Contas a pagar',
        icon: ArrowDownCircle,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: FINANCE_ROUTES.RECEIVABLES,
        label: 'Contas a receber',
        icon: ArrowUpCircle,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: RECEIVABLES_ROUTES.LIST,
        label: 'Recebimentos no cartao',
        icon: CircleDollarSign,
        requires: { module: 'finance', level: 'READ' },
      },
      {
        to: RECEIVABLES_ROUTES.MACHINES,
        label: 'Maquininhas e taxas',
        icon: CreditCard,
        requires: { module: 'finance', level: 'READ' },
      },
    ],
  },
  {
    title: 'Gestao',
    icon: ChartNoAxesColumn,
    items: [
      {
        to: MANAGEMENT_ROUTES.APPOINTMENTS_REPORT,
        label: 'Pesquisar atendimentos',
        icon: ClipboardList,
        requires: { module: 'reports', level: 'READ' },
        group: 'Relatorios',
      },
      {
        to: MANAGEMENT_ROUTES.REVENUE_REPORT,
        label: 'Faturamento e vendas',
        icon: Wallet,
        requires: { module: 'reports', level: 'READ' },
        group: 'Relatorios',
      },
      {
        // A apuracao e a porta de entrada; as regras ficam a um clique dela.
        to: COMMISSIONS_ROUTES.REPORT,
        label: 'Comissoes a pagar',
        icon: HandCoins,
        requires: { module: 'reports', level: 'READ' },
        group: 'Relatorios',
      },
      {
        to: MANAGEMENT_ROUTES.CASH_FLOW,
        label: 'DFC - fluxo de caixa',
        icon: ChartNoAxesColumn,
        requires: { module: 'reports', level: 'READ' },
        group: 'Relatorios',
      },
      {
        to: MANAGEMENT_ROUTES.INCOME_STATEMENT,
        label: 'DRE - resultado',
        icon: FileSpreadsheet,
        requires: { module: 'reports', level: 'READ' },
        group: 'Relatorios',
      },
      {
        to: LEADS_ROUTES.CONVERSION,
        label: 'Conversao do funil',
        icon: Target,
        requires: { module: 'reports', level: 'READ' },
        group: 'Relatorios',
      },
      {
        to: MANAGEMENT_ROUTES.AUDIT,
        label: 'Auditoria de alteracoes',
        icon: ScrollText,
        requires: { module: 'reports', level: 'READ' },
        group: 'Outros',
      },
    ],
  },
  {
    title: 'Suporte',
    icon: Headset,
    to: SUPPORT_ROUTES.TICKETS,
    items: [],
  },
  {
    // Empresa e acessos no mesmo lugar: as duas coisas so se mexem na hora de
    // configurar o sistema, e dois menus para isso viravam garimpo.
    title: 'Configuracoes',
    icon: Settings,
    items: [
      {
        to: COMPANY_ROUTES.COMPANY,
        label: 'Dados da empresa',
        icon: Building2,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        to: COMPANY_ROUTES.UNITS,
        label: 'Unidades',
        icon: MapPin,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        to: COMPANY_ROUTES.GOALS,
        label: 'Metas mensais',
        icon: Target,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        to: USERS_ROUTES.LIST,
        label: 'Usuarios do sistema',
        icon: Users,
        requires: { module: 'users', level: 'READ' },
      },
      {
        to: PROFILES_ROUTES.LIST,
        label: 'Perfis de acesso',
        icon: UsersRound,
        requires: { module: 'profiles', level: 'READ' },
      },
      {
        to: SETTINGS_ROUTES.PERMISSIONS,
        label: 'Mapa de permissoes',
        icon: ShieldCheck,
        requires: { module: 'settings', level: 'READ' },
      },
      {
        // Fatura nao e relatorio: virou submenu de Configuracoes porque e
        // configuracao do contrato do sistema, nao numero da operacao.
        to: BILLING_ROUTES.LICENSE,
        label: 'Licenca do sistema',
        icon: BadgeCheck,
        requires: { module: 'settings', level: 'READ' },
        group: 'Licenciamento',
      },
    ],
  },
];

/**
 * A conta de quem esta logado. Fica fora das secoes: no desktop e o menu do
 * avatar na barra do topo; no celular, o rodape da gaveta de navegacao.
 */
export const ACCOUNT_ITEMS: NavItem[] = [
  { to: SETTINGS_ROUTES.PROFILE, label: 'Meu perfil', icon: User },
  { to: SETTINGS_ROUTES.APPEARANCE, label: 'Preferencias', icon: Palette },
];

/**
 * As telas de quem administra o SaaS, na ordem do dia de trabalho. Quase
 * todas sao um menu direto - esconder uma tela atras de um bloco que abre so
 * acrescentava um clique. Marketing e a excecao: nasce com a landing e vai
 * receber as outras pecas de divulgacao.
 */
const SAAS_SECTIONS: NavSection[] = [
  { title: 'Board', icon: KanbanSquare, to: BOARD_ROUTES.BOARD, items: [] },
  { title: 'Venda', icon: MessageSquare, to: SAAS_ROUTES.VENDA, items: [] },
  { title: 'Suporte', icon: Headset, to: SUPPORT_ROUTES.SAAS_TICKETS, items: [] },
  {
    title: 'Cadastros',
    icon: FolderClosed,
    items: [
      { to: SAAS_ROUTES.SUBSCRIBERS, label: 'Assinantes', icon: Building2 },
      { to: SAAS_ROUTES.USERS, label: 'Usuarios', icon: ShieldCheck },
    ],
  },
  {
    title: 'Marketing',
    icon: Megaphone,
    items: [
      {
        to: MARKETING_ROUTES.SALON_LANDING,
        label: 'Landing page do SaaS',
        icon: Globe,
        external: true,
      },
    ],
  },
  {
    title: 'Financeiro',
    icon: CircleDollarSign,
    items: [
      { to: SAAS_ROUTES.FINANCEIRO_CATEGORIAS, label: 'Categorias financeiras', icon: Layers },
      { to: SAAS_ROUTES.FINANCEIRO_CONTAS, label: 'Cadastro de contas', icon: Receipt },
      { to: SAAS_ROUTES.FINANCEIRO_A_PAGAR, label: 'Contas a pagar', icon: ArrowDownCircle },
      { to: SAAS_ROUTES.FINANCEIRO_A_RECEBER, label: 'Contas a receber', icon: ArrowUpCircle },
    ],
  },
  {
    title: 'Gestao',
    icon: ChartNoAxesColumn,
    items: [
      { to: SAAS_ROUTES.GESTAO_RAIO_X, label: 'Relatório de Assinaturas', icon: ScanSearch },
      { to: SAAS_ROUTES.GESTAO_FATURAMENTO, label: 'Faturamento e vendas', icon: Wallet },
      { to: SAAS_ROUTES.GESTAO_DFC, label: 'DFC - fluxo de caixa', icon: ChartNoAxesColumn },
      { to: SAAS_ROUTES.GESTAO_DRE, label: 'DRE - resultado', icon: FileSpreadsheet },
      { to: SAAS_ROUTES.AUDIT, label: 'Auditoria', icon: ScrollText },
    ],
  },
  {
    title: 'Configuracoes',
    icon: Settings,
    items: [
      { to: SAAS_ROUTES.SETTINGS, label: 'Configurações do Sistema', icon: CircleDollarSign },
      { to: SAAS_ROUTES.SETTINGS_ASSISTANT, label: 'Assistente de Configuracoes', icon: Wand2 },
    ],
  },
];

/** As secoes que o usuario alcanca, ja sem os itens que ele nao pode abrir. */
export function useNavSections(): NavSection[] {
  const { permissions, user } = useSession();

  if (isSaasAdmin(user)) return SAAS_SECTIONS;

  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        !item.requires ||
        (permissions &&
          hasModulePermission(permissions, item.requires.module, item.requires.level)),
    ),
  }))
    .filter(
      (section) =>
        !section.requires ||
        (permissions &&
          hasModulePermission(permissions, section.requires.module, section.requires.level)),
    )
    .filter((section) => section.to || section.items.length > 0);
}

/**
 * Qual secao esta aberta no menu. A escolha manual vale enquanto a rota nao
 * muda; sem escolha, abre a secao da tela atual. Mora aqui porque a barra
 * lateral usa para desenhar e o layout, para saber a largura.
 */
export function useOpenSection() {
  const sections = useNavSections();
  const { pathname } = useLocation();
  const manual = useLayoutStore((state) => state.manualSection);
  const setManualSection = useLayoutStore((state) => state.setManualSection);

  const fromRoute = sections.find((section) =>
    section.items.some((item) => (item.end ? pathname === item.to : pathname.startsWith(item.to))),
  )?.title;

  /**
   * O submenu so abre por clique. Abrir sozinho pela rota fazia "Ver agenda"
   * e "Ver atendimentos" chegarem na tela nova com o painel por cima dela.
   */
  const openSection =
    manual && manual.path === pathname ? (manual.section ?? undefined) : undefined;

  return {
    sections,
    openSection,
    /** A secao da tela atual - marcada mesmo com o submenu fechado. */
    activeSection: fromRoute,
    toggleSection: (title: string) =>
      setManualSection({ path: pathname, section: openSection === title ? null : title }),
    closeSection: () => setManualSection({ path: pathname, section: null }),
    /**
     * Fecha o submenu ja na rota de destino: sem isso a nova tela reabriria
     * a secao dela, e o menu ficaria aberto por cima do conteudo.
     */
    closeAfterNavigate: (to: string) => setManualSection({ path: to, section: null }),
  };
}

/**
 * Os atalhos do topo do painel: o que o balcao abre toda hora e o que o
 * marketing acompanha, na ordem do dia de trabalho. E uma ordem curada - o
 * sistema nao mede uso.
 */
const ATALHOS: { to: string; label?: string }[] = [
  { to: SCHEDULE_ROUTES.AGENDA },
  { to: SCHEDULE_ROUTES.SERVICE_DESK },
  { to: CONVERSATIONS_ROUTES.LIST },
  { to: LEADS_ROUTES.BOARD },
  { to: CAMPAIGNS_ROUTES.LIST, label: 'Campanhas' },
  { to: CASHBACK_ROUTES.RULES, label: 'Cashback' },
  { to: COUPONS_ROUTES.LIST },
];

/** Os atalhos que o usuario alcanca, na ordem acima. */
export function useQuickActions(): NavItem[] {
  const sections = useNavSections();
  const porRota = new Map(
    sections.flatMap((section) => section.items).map((item) => [item.to, item]),
  );

  return ATALHOS.flatMap((atalho) => {
    const item = porRota.get(atalho.to);

    return item ? [{ ...item, label: atalho.label ?? item.label }] : [];
  });
}
